import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { JwtService } from '@app/core/auth';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '@app/services/language.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header class="bg-white shadow-sm border-b border-gray-200 fixed top-0 right-0 left-0 lg:left-64 z-30">
      <div class="pl-14 pr-3 sm:px-6 py-3 sm:py-4 lg:pl-6">
        <div class="flex items-center justify-between">
          <!-- Breadcrumb -->
          <div class="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
            <nav class="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm overflow-hidden ml-8 sm:ml-0">
              <a href="#" class="text-gray-500 hover:text-gray-700 whitespace-nowrap hidden sm:inline">{{ 'HEADER.BRAND' | translate }}</a>
              <svg class="w-3 h-3 sm:w-4 sm:h-4 text-gray-300 flex-shrink-0 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
              <span class="text-gray-900 font-medium truncate">{{ currentPageTitle }}</span>
            </nav>
          </div>

          <!-- Actions -->
          <div class="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            <select (change)="switchLang($any($event.target).value)" [value]="currentLang" class="border rounded px-1.5 sm:px-2 py-1 text-xs sm:text-sm">
              <option value="fr">FR</option>
              <option value="en">EN</option>
            </select>
          </div>
        </div>
      </div>
    </header>
  `
})
export class HeaderComponent implements OnInit {
  currentPageTitle = '';
  currentLang = 'fr';

  user = {
    name: '',
    organisation: '',
    role: ''
  };

  private router = inject(Router);
  private jwtService = inject(JwtService);
  public translate = inject(TranslateService);
  private languageService = inject(LanguageService);
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.updatePageTitle(this.router.url);

    // Router events subscription
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        this.updatePageTitle(event.url);
      });

    // S'abonner aux changements de langue
    this.languageService.currentLanguage$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(lang => {
        this.currentLang = lang;
        this.updatePageTitle(this.router.url);
      });

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updatePageTitle(this.router.url);
      });

    this.jwtService.token$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(decodedToken => {
        try {
          if (decodedToken && decodedToken.name && decodedToken.sub) {
            // Utilise le nom complet du token s'il existe
            this.user.name = decodedToken.name;
            const match = decodedToken.sub.match(/@([^.]+)\./);
            const organisation = match ? match[1] : "";
            this.user.organisation = decodedToken.organization ?? organisation.toUpperCase();
            if (this.user.organisation === "WARIFLOW" || this.user.organisation === "SPEEDPRESTA") {
              // call admin
            }
          } else {
            // Si pas de token, réinitialiser le nom
            this.user.name = '';
            this.user.organisation = '';
          }
        } catch (error) {
          console.error('Error processing token in header component:', error);
          this.user.name = '';
          this.user.organisation = '';
        }
      });
  }

  private updatePageTitle(url: string) {
    const titleMap: { [key: string]: string } = {
      '/dashboard': 'HEADER.DASHBOARD',
      '/invoices': 'HEADER.INVOICES',
      '/quotes': 'HEADER.QUOTES',
      '/customers': 'HEADER.CUSTOMERS',
      '/settings': 'HEADER.SETTINGS'
    };

    let key = 'HEADER.DASHBOARD'; // Default key
    for (const path in titleMap) {
        if (url.startsWith(path)) {
            key = titleMap[path];
            break;
        }
    }

    this.translate.get(key).subscribe(translation => {
      this.currentPageTitle = translation;
    });
  }

  switchLang(lang: string) {
    // Utiliser le LanguageService pour synchroniser avec localStorage et le paramètre URL
    this.languageService.setLanguage(lang);
  }

  getActualOrganizationByJwt(): string {
    return this.jwtService.decodeToken()?.organization ?? "";
  }
}
